import { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { useUserRoles } from "@/hooks/useUserRoles";
import { supabase } from "@/integrations/supabase/client";

/**
 * Guards the /instructor/* route surface.
 *
 * Authorization is based ONLY on the presence of an authorized role in
 * `user_roles` (RLS is the server-side source of truth). An approved
 * `role_applications` row does NOT by itself grant access — the role must
 * actually exist. Pending / contacted / rejected applicants are routed to
 * the existing /pending-approval page instead of the instructor UI.
 */
export default function RequireInstructor({ children }: { children: ReactNode }) {
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();
  const { data: userRoles, isLoading: rolesLoading } = useUserRoles();

  const hasAuthorizedRole =
    !!userRoles?.some((r) => r === "instructor" || r === "admin" || r === "super_admin");

  // Only look up the application when the user is signed in AND has no
  // authorized role — used purely to decide whether to send them to
  // /pending-approval vs. bounce them home.
  const shouldCheckApplication = !!user && !rolesLoading && !hasAuthorizedRole;

  const {
    data: latestApplication,
    isLoading: appLoading,
    isError: appErrored,
  } = useQuery({
    queryKey: ["require-instructor-application", user?.id],
    enabled: shouldCheckApplication,
    staleTime: 30_000,
    retry: 1,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("role_applications")
        .select("status")
        .eq("user_id", user!.id)
        .eq("requested_role", "instructor")
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) {
        if (import.meta.env.DEV) {
          // Generic, non-sensitive diagnostic only.
          console.warn("[RequireInstructor] application lookup failed");
        }
        throw error;
      }
      return data;
    },
  });

  if (authLoading || (user && rolesLoading)) return null;

  if (!user) {
    const redirectTo = `${location.pathname}${location.search}`;
    return <Navigate to={`/auth?redirect=${encodeURIComponent(redirectTo)}`} replace />;
  }

  if (hasAuthorizedRole) return <>{children}</>;

  if (shouldCheckApplication && appLoading && !appErrored) return null;

  // Secure default-deny: if the application lookup failed, do not grant access
  // and do not route to /pending-approval based on missing data.
  if (shouldCheckApplication && appErrored) {
    return <Navigate to="/" replace />;
  }

  const status = latestApplication?.status;
  if (status === "pending" || status === "contacted" || status === "rejected") {
    return <Navigate to="/pending-approval" replace />;
  }

  // Approved-but-no-role, or no application at all → send home safely.
  return <Navigate to="/" replace />;
}
